import React from 'react';
import { Col, Row, HStack } from '@saucelabs/sl-component-grid';
import { Typography } from '@saucelabs/ds-typography';
import { IconCreditCardOutline } from '@saucelabs/ds-icons';
import amexCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-amex.png';
import dinersCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-diners.png';
import discoverCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-discover.png';
import jcbCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-jcb.png';
import mastercardCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-mastercard.png';
import visaCardIcon from '@saucelabs/sl-icons/lib/icn-credit-card-visa.png';
import { Card } from '@saucelabs/ds-card';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ROUTE_PARENT_SECTION, ROUTE_SECTION_DETAILS } from '../../constants';
import { getCountryByCode, getStateName } from '../../utils/locations';
import CouponCode from './CouponCode';
import { paymentFormSelector } from '../../ducks/billing-info/selectors';

const EditDetails = () => (
  <Link
    to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_DETAILS}`}
  >
    Edit
  </Link>
);

interface ReviewContactDetailsProps {
  applyCoupon,
  checkoutPreview,
  couponCode: string,
}

export const ReviewContactDetails: React.FC<ReviewContactDetailsProps> = ({
  applyCoupon,
  checkoutPreview,
  couponCode,
}) => {
  const billingInfo = useSelector(paymentFormSelector);

  const creditCardInfo: CreditCardSummary = {
    cardType: billingInfo.paymentMethod.cardType,
    lastFour: billingInfo.paymentMethod.lastFour,
    ownerFirstName: billingInfo.paymentInfo.firstName,
    ownerLastName: billingInfo.paymentInfo.lastName,
  };

  if (billingInfo.paymentMethodCurrent) {
    creditCardInfo.cardType = billingInfo.paymentMethodCurrent.cardType;
    creditCardInfo.lastFour = billingInfo.paymentMethodCurrent.lastFour;
  }
  if (billingInfo.paymentInfoCurrent) {
    creditCardInfo.ownerFirstName = billingInfo.paymentInfoCurrent.firstName;
    creditCardInfo.ownerLastName = billingInfo.paymentInfoCurrent.lastName;
  }

  const stateName: string = getStateName(
    billingInfo.initialValues.country,
    billingInfo.initialValues.state,
  );
  const contactDetails = [
    billingInfo.initialValues.company,
    billingInfo.initialValues.email,
    billingInfo.initialValues.address1,
    billingInfo.initialValues.city,
    `${stateName} ${String(billingInfo.initialValues.postalCode)}`,
    getCountryByCode(billingInfo.initialValues.country),
  ];
  const cardImage = {
    'American Express': amexCardIcon,
    'Diners Club': dinersCardIcon,
    Discover: discoverCardIcon,
    JCB: jcbCardIcon,
    MasterCard: mastercardCardIcon,
    Visa: visaCardIcon,
  }[creditCardInfo.cardType];

  return (
    <Card className="w-full -ml-1">
      <Row>
        <Col
          md={6}
        >
          <div
            className="m-2.5"
            data-qa="contact-details-review"
          >
            <Typography
              className="text-high-emphasis"
              variant="heading-5"
            >
              Company & Contact Details
            </Typography>
            <div>
              {contactDetails.map((detail) => (
                <Typography
                  key={detail}
                  className="text-high-emphasis mb-0.5"
                  component="p"
                  disableSpacing
                  variant="text"
                >
                  {detail}
                </Typography>
              ))}
            </div>
            <div className="mt-1">
              <EditDetails />
            </div>
          </div>
        </Col>
        <Col
          md={6}
        >
          <div className="m-2.5">
            <Typography
              className="text-high-emphasis font-semibold"
              variant="body-large"
            >
              Payment Method
            </Typography>
            <div
              className="mt-1"
              data-qa="payment-method-review"
            >
              <HStack
                alignItems="flex-end"
                justifyContent="flex-start"
              >
                {cardImage ? (
                  <img
                    alt="Card"
                    className="mr-1"
                    height={16}
                    src={cardImage}
                    width={24}
                  />
                ) : (
                  <IconCreditCardOutline
                    className="mr-1 fill-current text-gray-500"
                    height="20"
                  />
                )}
                <Typography
                  className="text-high-emphasis"
                  component="span"
                >
                  {`•••• •••• •••• ${creditCardInfo.lastFour} `}
                </Typography>
              </HStack>
              <Typography
                className="text-high-emphasis mt-1"
              >
                {`${creditCardInfo.ownerFirstName} ${creditCardInfo.ownerLastName}`}
              </Typography>
            </div>
            <div className="mt-1">
              <EditDetails />
            </div>
            <div
              className="mt-3"
            >
              <CouponCode
                appliedCoupon={couponCode}
                appliedCouponGivesDiscount={checkoutPreview?.discount > 0}
                applyCoupon={applyCoupon}
              />
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );
};
